/**
 * Owner: Masa Bayu
 * File: CommunityHome.tsx
 * Date:Time: 2026-10-17 04:18
 * Description: Dedicated community home — overview, feed filter tabs, compose (signed-in)
 *   or join gate (guest), all wired to the real QXCircle client API.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAccountProfile } from '../auth/use-account-profile';
import {
  createQxCirclePost,
  getQxCircleFeed,
  getQxCircleOverview,
  type QxCircleFeedFilter,
  type QxCircleFeedPost,
  type QxCircleOverview,
} from '../client/qxcircle-api';
import { CommunityPostCard } from './CommunityPostCard';

export interface CommunityHomeProps {
  readonly onNavigate: (path: string) => void;
}

export function CommunityHome({ onNavigate }: CommunityHomeProps) {
  const account = useAccountProfile();
  const [overview, setOverview] = useState<QxCircleOverview | null>(null);
  const [filter, setFilter] = useState<QxCircleFeedFilter>('all');
  const [posts, setPosts] = useState<readonly QxCircleFeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    Promise.all([getQxCircleOverview(), getQxCircleFeed(filter)])
      .then(([nextOverview, feed]) => {
        if (!alive) return;
        setOverview(nextOverview);
        setPosts(feed.items);
      })
      .catch(() => {
        if (alive) setPosts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filter]);

  useEffect(() => load(), [load]);

  const submit = async () => {
    const body = draft.trim();
    if (body.length === 0 || posting) return;
    setPosting(true);
    try {
      await createQxCirclePost(body);
      setDraft('');
      load();
    } catch {
      /* keep draft on failure */
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community</p>
        <h2 className="ddx__title">{overview?.title ?? 'QXCircle'}</h2>
        <p className="ddx__lede">
          {overview?.tagline ??
            'Share updates, follow authors, and join the conversation around your books.'}
        </p>
      </header>

      <div className="ddx__actions">
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/circle/explore');
          }}
        >
          Explore
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/circle/saved');
          }}
        >
          Saved
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/circle/notifications');
          }}
        >
          Alerts
        </button>
      </div>

      {account.signedIn ? (
        <div className="cmc__gate">
          <h3>Post as {account.displayName ?? 'Member'}</h3>
          <div className="cmc__compose">
            <textarea
              value={draft}
              placeholder="Share an update with the circle…"
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
            <div className="ddx__actions">
              <button
                type="button"
                className="ddx__cta ddx__cta--solid"
                disabled={posting || draft.trim().length === 0}
                onClick={() => {
                  void submit();
                }}
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="cmc__gate">
          <h3>Join the circle to post</h3>
          <p>Guests can read the feed. Create an account or sign in to publish on QXCircle.</p>
          <div className="ddx__actions">
            <button
              type="button"
              className="ddx__cta ddx__cta--solid"
              onClick={() => {
                onNavigate('/account');
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      )}

      <div className="cmc__tabs" role="tablist" aria-label="Feed filter">
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'all'}
          className="cmc__tab"
          data-on={filter === 'all'}
          onClick={() => {
            setFilter('all');
          }}
        >
          All
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === 'following'}
          className="cmc__tab"
          data-on={filter === 'following'}
          onClick={() => {
            setFilter('following');
          }}
        >
          Following
        </button>
      </div>

      {loading ? (
        <p className="ddx__status">Loading feed…</p>
      ) : posts.length === 0 ? (
        <div className="ddx__empty">
          <h3>No posts yet</h3>
          <p>
            {filter === 'following'
              ? 'Follow authors to see their updates here.'
              : 'Be the first to share something with the circle.'}
          </p>
        </div>
      ) : (
        <div className="cmc__feed">
          {posts.map((post) => (
            <CommunityPostCard key={post.id} post={post} onNavigate={onNavigate} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
